import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://conexusglobalconsulting.com';

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  path?: string;
  type?: 'website' | 'article';
  image?: string;
  keywords?: string;
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const localeMap: Record<string, string> = {
  en: 'en_US',
  pt: 'pt_BR',
  es: 'es_ES',
};

const SEOHead = ({
  title,
  description,
  path = '/',
  type = 'website',
  image,
  keywords,
  noindex,
  breadcrumbs,
  jsonLd,
}: SEOHeadProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';
  const canonicalUrl = `${SITE_URL}${path}`;
  const ogImage = image || `${SITE_URL}/og-default.png`;
  const fullTitle = path === '/' ? title : `${title} | Conexus Global Consulting`;
  const extraJsonLd = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const breadcrumbJsonLd =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: b.name,
            item: `${SITE_URL}${b.path}`,
          })),
        }
      : null;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />}

      {/* Hreflang alternates — tells Google these URLs are language variants of the same page */}
      <link rel="alternate" hrefLang="en" href={`${canonicalUrl}?lng=en`} />
      <link rel="alternate" hrefLang="en-US" href={`${canonicalUrl}?lng=en`} />
      <link rel="alternate" hrefLang="pt" href={`${canonicalUrl}?lng=pt`} />
      <link rel="alternate" hrefLang="pt-BR" href={`${canonicalUrl}?lng=pt`} />
      <link rel="alternate" hrefLang="pt-PT" href={`${canonicalUrl}?lng=pt`} />
      <link rel="alternate" hrefLang="es" href={`${canonicalUrl}?lng=es`} />
      <link rel="alternate" hrefLang="es-ES" href={`${canonicalUrl}?lng=es`} />
      <link rel="alternate" hrefLang="es-MX" href={`${canonicalUrl}?lng=es`} />
      <link rel="alternate" hrefLang="es-AR" href={`${canonicalUrl}?lng=es`} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Conexus Global Consulting" />
      <meta property="og:locale" content={localeMap[lang] || 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />

      {/* JSON-LD Organization (homepage only) */}
      {path === '/' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'EducationalOrganization',
            name: 'Conexus Global Consulting',
            url: SITE_URL,
            logo: `${SITE_URL}/logo.png`,
            foundingDate: '2024',
            numberOfEmployees: {
              '@type': 'QuantitativeValue',
              value: '10-50',
            },
            areaServed: [
              { '@type': 'Country', name: 'United States' },
              { '@type': 'Country', name: 'Brazil' },
              { '@type': 'Country', name: 'Mexico' },
              { '@type': 'Country', name: 'Colombia' },
              { '@type': 'Country', name: 'Argentina' },
              { '@type': 'Country', name: 'Portugal' },
              { '@type': 'Country', name: 'Spain' },
            ],
            knowsLanguage: ['en', 'pt', 'es'],
            description,
            address: {
              '@type': 'PostalAddress',
              streetAddress: '4700 Millenia Blvd, Suite 500',
              addressLocality: 'Orlando',
              addressRegion: 'FL',
              postalCode: '32839',
              addressCountry: 'US',
            },
            telephone: '+14078190008',
            email: 'info@conexusglobalconsulting.com',
            sameAs: [
              'https://www.instagram.com/conexusglobalconsulting',
              'https://www.facebook.com/conexusglobalconsulting',
              'https://www.youtube.com/@conexusglobalconsulting',
              'https://www.tiktok.com/@conexusglobalconsulting',
            ],
          })}
        </script>
      )}

      {/* JSON-LD WebSite with SearchAction (homepage only) */}
      {path === '/' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Conexus Global Consulting',
            url: SITE_URL,
            inLanguage: ['en', 'pt-BR', 'es'],
          })}
        </script>
      )}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      )}

      {extraJsonLd.map((data, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;

